<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Card from 'primevue/card';
import { useDragAndDrop } from '@formkit/drag-and-drop/vue';
import { animations } from '@formkit/drag-and-drop';
import { useFormBuilder } from '@/stores/formBuilder';
import { ButtonType, CustomButton } from '@/components';
import { FIELD_TYPES } from '@/models';
import { cloneDeep } from 'lodash';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
};


const builder = useFormBuilder();
const initialFields = ref(cloneDeep(builder.form!.items));

const [parent, fields] = useDragAndDrop(builder.form!.items!, {
  plugins: [animations()]
});

const currentSelectedType = (fieldType: string) => {
  return FIELD_TYPES.find(type => type.type === fieldType);
};

const onDragEnd = () => {};
const isPristine = computed(() => {
  return JSON.stringify(initialFields.value) !== JSON.stringify(fields.value);
});

const saveOrder = () => {
    builder.form!.items = fields.value;
    closeDialog();
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      header="Re-order fields"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <div class="content-root">
        <span class="p-text-secondary block span">Drag fields to set order that they should appear when entering
          data.</span>
        <ul ref="parent">
          <li v-for="field in fields"
            :key="field.identifier"
            class="row"
            @dragend="onDragEnd">
            <div class="form-field-root">
              <Card>
                <template #content>
                  <div class="card-root">
                    <div class="field-type">
                      <CustomButton :text="field.type"
                        :icon="{ style: currentSelectedType(field.type)!.iconStyle, icon: currentSelectedType(field.type)!.icon }"
                        :font-weight="700"
                        class="field-type-button"
                        :type="ButtonType.WITHOUT_OUTLINE"/>
                    </div>

                    <div class="field-label">
                      {{ field.label }}
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </li>
        </ul>
      </div>
      <div class="footer">
        <div class="empty"></div>
        <div class="footer-buttons">
          <CustomButton text="Cancel"
            @onClick="closeDialog"
            :margin-left="16"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            @onClick="saveOrder"
            :margin-left="16"
            :disabled="!isPristine"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../../assets/dialog";
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.p-card {
  border-radius: 4px;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
  padding: 20px 28px;

  :deep(.p-card-body) {
    padding: 0px;
  }
}
.content-root {
  width: 1000px;
  display: flex;
  flex-direction: column;

  .span {
    padding: 0px 40px 20px 40px;
  }
}
ul {
  padding: 12px 40px;
  list-style: none;
  margin-bottom: 48px;
  max-height: 500px;
  overflow-y: auto;
}
.form-field-root {
  padding-bottom: 16px;
}
.card-root {
  display: flex;
  align-items: center;
  cursor: grab;

  .field-type {
    width: 150px;
  }

  .field-label {
    font-weight: 700;
  }
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
</style>
